import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  InspectionWorkListCard,
  MiBuildPageHeader,
  MiList,
} from "@miplatform/components";
import {
  IInspectionAttempt,
  IVerticalTabProps,
  InspectionActionTypes,
} from "@miplatform/types";
import { getOrderTypeFilterTabs } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { VerticalTabsLayout } from "@mibuildweb/components";
import { Box } from "@mui/material";
import {
  InspectionsProvider,
  useInspectionsContext,
} from "../../Inspections/InspectionsProvider";
import { InspectionDrawer } from "../../Inspections/components/InspectionDrawer/InspectionDrawer";
import {
  IInspectionForm,
  getInspectionFormDefaultValues,
  inspectionFormSchema,
} from "../../Inspections/components/InspectionDrawer/inspectionFormSchemas";

export const HomeInspectionsTab = ({
  inspections,
  refresh,
}: {
  inspections: IInspectionAttempt[];
  refresh: () => void;
}) => {
  return (
    <InspectionsProvider>
      <HomeInspectionsTabContent
        inspections={inspections}
        refreshInspections={refresh}
      />
    </InspectionsProvider>
  );
};

const HomeInspectionsTabContent = ({
  inspections,
  refreshInspections,
}: {
  inspections: IInspectionAttempt[];
  refreshInspections: () => void;
}) => {
  const { classes, theme } = useStyles();

  const [filter, setFilter] = useState<string | number | null>(null);

  const {
    setSelectedInspectionAttemptListItem,
    setDrawerType,
    inspectionDrawerOpen,
    setInspectionDrawerOpen,
    setIsConfirmationModalOpen,
    drawerType,
    getInspectionTemplates,
  } = useInspectionsContext();

  useEffect(() => {
    getInspectionTemplates();
  }, []);

  const handleDrawerClose = (refresh = false) => {
    setInspectionDrawerOpen(false);
    setIsConfirmationModalOpen(false);
    reset(getInspectionFormDefaultValues({ type: null }));
    if (refresh) {
      refreshInspections();
    }
  };

  const formControl = useForm<IInspectionForm>({
    defaultValues: getInspectionFormDefaultValues({ type: drawerType }),
    resolver: zodResolver(inspectionFormSchema),
    mode: "onChange",
  });
  const { reset } = formControl;

  const tabs = useMemo<IVerticalTabProps<IInspectionAttempt>[]>(() => {
    if (!inspections) {
      return [];
    }
    const templateFilterOptions =
      getOrderTypeFilterTabs<IInspectionAttempt>(inspections);

    const tabs: IVerticalTabProps<IInspectionAttempt>[] = [
      { label: "All Inspections", value: null },
      ...templateFilterOptions,
    ];
    return tabs;
  }, [inspections]);

  const visibleInspections = useMemo(() => {
    if (filter) {
      return inspections.filter((inspection) => {
        return inspection.templateId === filter;
      });
    }
    return inspections;
  }, [filter, inspections]);

  const onMenuItemClick = ({
    inspectionAttempt,
    actionType,
  }: {
    inspectionAttempt: IInspectionAttempt;
    actionType: InspectionActionTypes | null;
  }) => {
    setInspectionDrawerOpen(true);
    setSelectedInspectionAttemptListItem(inspectionAttempt);
    setDrawerType(actionType);
  };

  const handleTabChange = (
    tab?: IVerticalTabProps<IInspectionAttempt> | null
  ) => {
    setFilter(tab?.value || null);
  };

  return (
    <>
      <Box className={classes.root}>
        <VerticalTabsLayout
          tabs={tabs}
          useLegacyStyles
          hideStatusIcons
          hideNavigationButtons
          onTabChange={handleTabChange}
        >
          <MiBuildPageHeader
            style={{ marginTop: -20 }}
            title="Inspections"
            hasDivider={false}
            titleFontSize={theme.fontSize.xlarge}
          />
          <MiList
            viewType="card"
            data={visibleInspections}
            CardComponent={InspectionWorkListCard}
            cardProps={{ onMenuItemClick }}
            groupedMode
            groupedCriteria="attemptStatus"
            showGroupCount
          />
        </VerticalTabsLayout>
      </Box>
      <InspectionDrawer
        open={inspectionDrawerOpen}
        onClose={handleDrawerClose}
        formControl={formControl}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.grey.lightAccent,
    overflow: "hidden",
  },
  contentContainer: {
    flex: "1",
    overflowY: "auto",
    position: "relative",
  },
  statusTag: {
    padding: theme.spacing(0, 1),
    borderRadius: 4,
    height: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  actionMenu: {
    position: "absolute",
  },
}));
